import { useEffect, useState } from 'react';

import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';

import SubHeader from 'src/components/subheader/SubHeader';
import { GenreLangFilterType } from 'src/services/apiRequests/TvShowDetailsPage/GenreLanguageService';
import { UsersData } from 'src/store/interface';
import {
  AssetsSubType,
  EpisodesPageContainerProps,
  TV_SHOW_PAGE_CONSTANTS
} from 'src/utils/constants/TvShowDetailsPageConstants';
import { DEVICE_WIDTH_CONSTANTS } from 'src/utils/constants/utilConstants';
import { screenViewEventTrackerDetailsPage } from 'src/utils/TvShowDetailsHelper/tvShowDetailsHandler';

import { useAppStore } from 'src/hooks/useAppStore';
import { Episode } from '../../services/GraphQL/Queries/types';
import { DisplayLanguage } from '../../utils/constants/stringConstants';
import {
  generateInterval,
  getActiveSeasonIndex,
  getCurrentSeasonIndexFromUrl
} from '../../utils/TvShowDetailsHelper/episodePageHelper';
import BreadCrumbs from '../breadCrumbs/BreadCrumbs';
import EpisodesListContainer from '../EpisodesListContainer/EpisodesListContainer';
import SEOMeta from '../pageMeta/SEOMeta';
import ScrollToTopButton from '../scrollToTopButton/ScrollToTopButton';
import * as styledComponents from './EpisodesPageContainerStyle';

const DropDown = dynamic(() => import('../dropDown/DropDown/DropDown'));

const EpisodesPageContainer = ({
  breadCrumbItems,
  detailsData,
  seoData,
  subHeaderItems
}: EpisodesPageContainerProps) => {
  const {
    usersLocationData,
    usersPreferenceData,
    initialLaunchData,
    appConfig,
    usersData
  } = useAppStore();

  const countryCode = usersLocationData?.country_code || '';
  const innerWidth = appConfig?.deviceWidth as number;
  const isMobileScreen = innerWidth < DEVICE_WIDTH_CONSTANTS.deviceWidth1200;
  const displayLanguage =
    usersPreferenceData?.display_language || DisplayLanguage.ENGLISH;
  const allDisplayLanguages = initialLaunchData?.getlang.display || [];
  const router = useRouter();
  const seasonIndexFromRouter = router?.query?.episodeName_seasonNo as string;
  const [activeSeasonIndex, setActiveSeason] = useState<number>(
    getActiveSeasonIndex(
      getCurrentSeasonIndexFromUrl(seasonIndexFromRouter),
      detailsData
    )
  );

  // get active season
  const changeRoute = (index: number) => {
    const currentPath = router && router?.query;
    const { showId, showName } = currentPath;
    const urlIndex =
      (detailsData?.seasons?.length &&
        detailsData?.seasons[index - 1]?.index) ||
      1;
    if (urlIndex > 1) {
      router.push(
        `/tv-shows/details/${showName}/${showId}/season-${urlIndex}/episodes`
      );
    } else {
      router.push(`/tv-shows/details/${showName}/${showId}/episodes`);
    }
    setActiveSeason(index - 1);
  };

  useEffect(() => {
    screenViewEventTrackerDetailsPage(
      TV_SHOW_PAGE_CONSTANTS.SHOW_DETAILS,
      usersData as UsersData
    );
    if (
      detailsData &&
      getActiveSeasonIndex(
        getCurrentSeasonIndexFromUrl(seasonIndexFromRouter),
        detailsData
      ) !== activeSeasonIndex
    ) {
      setActiveSeason(
        getActiveSeasonIndex(
          getCurrentSeasonIndexFromUrl(seasonIndexFromRouter),
          detailsData
        )
      );
    }
  }, [JSON.stringify(detailsData), router?.query?.episodeName_seasonNo]);

  return (
    <>
      {detailsData && detailsData.seasons && detailsData.seasons.length && (
        <>
          <styledComponents.Container data-testid="episodePageContainer">
            <SEOMeta {...seoData} />
            <BreadCrumbs breadCrumbItems={breadCrumbItems}></BreadCrumbs>
            {isMobileScreen && (
              <SubHeader
                browseByType={GenreLangFilterType.genre}
                assetSubType={detailsData.assetSubType || AssetsSubType.TVSHOW}
                isAllRequired={true}
                contentName={detailsData?.title as string}
                contentId={detailsData?.id as string}
                subHeaderItems={subHeaderItems}
              />
            )}
            <styledComponents.Title data-testid="episodePageTitle">
              {detailsData.title}
            </styledComponents.Title>
            {detailsData.seasons.length > 1 && (
              <styledComponents.DropDownContainer data-testid="episodePageDropDown">
                <DropDown
                  intervalData={generateInterval(detailsData)}
                  changeEventHandler={changeRoute}
                  triggerMixpanelFlag={true}
                  seriesTitle={detailsData?.originalTitle as string}
                  activeSeasonIndex={activeSeasonIndex}
                />
              </styledComponents.DropDownContainer>
            )}
            {detailsData.seasons[activeSeasonIndex]?.episodes && (
              <EpisodesListContainer
                episodesList={
                  detailsData?.seasons[activeSeasonIndex]?.episodes as Episode[]
                }
                seasonId={detailsData?.seasons[activeSeasonIndex]?.id as string}
                totalEpisodes={
                  (detailsData?.seasons[activeSeasonIndex] &&
                    detailsData?.seasons[activeSeasonIndex]
                      ?.totalEpisodes) as number
                }
              />
            )}
          </styledComponents.Container>
          <ScrollToTopButton />
        </>
      )}
    </>
  );
};

export default EpisodesPageContainer;
